import type { CSSProperties } from "react";

import { transformLayoutSettingsToCss } from "../properties/transformCssProperties.ts";
import type { LayoutSettings, Orientation } from "../types.ts";
import type { ElementState, StylesByElementState } from "./types.ts";
import { createElementStateKey } from "./useActiveElementState.ts";

export type CreateCssFromStylesByElementStateOptions = {
  stylesByElementState?: StylesByElementState;
  nodeReferences?: Record<string, number>;
  baseLayoutSettings?: LayoutSettings;
  baseCssProperties?: CSSProperties;
  parentOrientation?: Orientation;
  enableFitContent?: boolean;
};

export const createCssFromStylesByElementState = ({
  stylesByElementState,
  nodeReferences,
  baseLayoutSettings,
  baseCssProperties = {},
  parentOrientation = "horizontal",
  enableFitContent = false,
}: CreateCssFromStylesByElementStateOptions): Record<string, Partial<CSSProperties>> => {
  if (!stylesByElementState) return {};

  return (
    Object.fromEntries(
      Object.entries(stylesByElementState).map(([key, value]) => {
        // Merge base CSS with element state CSS overrides
        const cssProperties = { ...value?.cssProperties };

        // If this element state has layout settings, transform them to CSS
        if (value?.layoutSettings && baseLayoutSettings) {
          const mergedLayoutSettings = { ...baseLayoutSettings, ...value.layoutSettings };
          const layoutCss = transformLayoutSettingsToCss(
            baseCssProperties,
            mergedLayoutSettings,
            parentOrientation,
            enableFitContent,
          );
          // Only include the CSS properties that differ from what we'd get with base layout settings
          const baseLayoutCss = transformLayoutSettingsToCss(
            baseCssProperties,
            baseLayoutSettings,
            parentOrientation,
            enableFitContent,
          );
          for (const prop of Object.keys(layoutCss) as (keyof CSSProperties)[]) {
            if (layoutCss[prop] !== baseLayoutCss[prop]) {
              (cssProperties as Record<string, unknown>)[prop] = layoutCss[prop];
            }
          }
        }

        // Check if there is a style override that references an ancestor element, and resolve the nodeId from nodeReferences.
        const otherNodeRefKey = value?.otherNodeRefKey;
        if (otherNodeRefKey) {
          if (!nodeReferences?.[otherNodeRefKey]) {
            console.warn(`Node reference ${otherNodeRefKey} not found for element state ${key}`);
          } else if (value.state !== "default") {
            return [createElementStateKey(value.state, nodeReferences[otherNodeRefKey]), cssProperties];
          }
        }
        return [key, cssProperties];
      }),
    ) ?? {}
  );
};

export const STATE_PRECEDENCE: Record<ElementState, number> = {
  default: 0,
  hover: 1,
  focus: 2,
  active: 3,
  disabled: 4,
  // Draggable states
  dragging: 5,
  dragPlaceholder: 6,
  draggingAllowed: 7,
  draggingNotAllowed: 8,
  dragCorrect: 9,
  dragIncorrect: 10,
  dragDropped: 11,
  // Droppable states
  dropTarget: 12,
  droppableAllowed: 13,
  droppableNotAllowed: 14,
  droppableOccupied: 15,
  droppableActive: 16,
  // HotZone states
  hotZoneActive: 17,
  hotZoneAllowed: 18,
  hotZoneNotAllowed: 19,
};

export const sortStylesByElementState = (
  stylesByElementState: StylesByElementState,
  ancestorIds: number[] = [],
  nodeReferences?: Record<string, number>,
): StylesByElementState => {
  if (!stylesByElementState) return {};

  const entries = Object.entries(stylesByElementState);

  const sortedEntries = entries.sort(([, configA], [, configB]) => {
    if (!configA || !configB) return 0;

    const hasAncestorA = configA.otherNodeRefKey !== undefined;
    const hasAncestorB = configB.otherNodeRefKey !== undefined;

    // First sort ancestor states vs self states
    if (hasAncestorA !== hasAncestorB) {
      return hasAncestorA ? -1 : 1;
    }

    // For ancestor states, sort by ancestorIds array order
    if (hasAncestorA && hasAncestorB && configA.otherNodeRefKey && configB.otherNodeRefKey && nodeReferences) {
      const indexA = ancestorIds.indexOf(nodeReferences[configA.otherNodeRefKey]);
      const indexB = ancestorIds.indexOf(nodeReferences[configB.otherNodeRefKey]);
      if (indexA !== indexB) {
        return indexA - indexB; // Lower index comes first
      }
    }

    // Sort by state precedence within each group
    const stateOrderA = STATE_PRECEDENCE[configA.state] ?? Number.MAX_SAFE_INTEGER;
    const stateOrderB = STATE_PRECEDENCE[configB.state] ?? Number.MAX_SAFE_INTEGER;
    return stateOrderA - stateOrderB;
  });

  return Object.fromEntries(sortedEntries);
};
